import { SkFont } from "@shopify/react-native-skia";
import React from "react";
import { Platform } from "react-native";
import { CartesianChart, Line, Scatter } from "victory-native";

import { Box } from "@/components/atoms";

// Platform-guarded require: victory (web SVG) crashes on native
// because it registers SVG elements like <line> that don't exist in RN
const {
  VictoryChart,
  VictoryLine,
  VictoryScatter,
  VictoryAxis,
  VictoryTooltip,
  VictoryVoronoiContainer,
  VictoryLabel,
} = Platform.OS === "web" ? require("victory") : {};

/**
 * Satoshi font family for consistent design system styling
 */
const SATOSHI_FONT_FAMILY = "Satoshi, sans-serif";

/* design-system: dynamic-style waiver — Victory chart library style API (not RN/NativeWind style prop); data-driven stroke color */
const victoryLineStyle = (color: string | undefined) =>
  ({
    data: {
      stroke: color || "#FF0000",
      strokeWidth: 2,
      strokeOpacity: 0.8,
    },
  }) as const;

/* design-system: dynamic-style waiver — Victory chart library style API (not RN/NativeWind style prop); data-driven fill color */
const victoryScatterStyle = (color: string | undefined) =>
  ({ data: { fill: color || "#3B82F6" } }) as const;

/* design-system: dynamic-style waiver — Victory chart library style API (not RN/NativeWind style prop); data-driven label colors shared by x and y axes */
const victoryAxisStyle = (labelColor?: string, fontSize?: number) =>
  ({
    tickLabels: {
      fill: labelColor || "#666",
      fontSize: fontSize ?? 10,
      fontFamily: SATOSHI_FONT_FAMILY,
    },
    axis: { stroke: "transparent" },
    ticks: { stroke: "transparent" },
  }) as const;

/**
 * Brightens a hex color for better tooltip visibility on dark backgrounds
 * @param hex - Hex color string
 * @returns Brightened hex color
 */
const brightenColor = (hex: string): string => {
  // Map of dark colors to their brighter tooltip-friendly versions
  // Purple (#a855f7) is NOT mapped - it's already visible on dark backgrounds
  const colorMap: Record<string, string> = {
    "#9ca3af": "#e5e7eb", // gray-400 -> gray-200
    "#6b7280": "#d1d5db", // gray-500 -> gray-300
  };
  return colorMap[hex.toLowerCase()] ?? hex;
};

/**
 * Default tick format function that converts values to strings
 * @param value - Value to format
 * @returns String representation of value
 */
const defaultTickFormat = <T,>(value: T): string => String(value);

/**
 * Creates a tooltip label formatter for Victory charts that looks up all values
 * at the same x position from the original data source
 * @param xKey - Key for x-axis values
 * @param yKeys - Array of y-axis keys to display
 * @param yKeyLabels - Optional labels for y keys
 * @param data - Original data array to look up all values
 * @returns Label formatter function
 */
export const createTooltipLabelFormatter =
  (
    xKey: string,
    yKeys: string[],
    yKeyLabels?: Record<string, string>,
    data?: PlatformChartData[]
  ): ((args: { datum: PlatformChartData }) => string) =>
  ({ datum }: { datum: PlatformChartData }) => {
    if (!datum || datum[xKey] === undefined) return "";

    // Find the full data point from original data to get ALL yKey values
    const xValue = datum[xKey];
    const fullDatum = data?.find(d => d[xKey] === xValue) ?? datum;

    const lines = yKeys
      .filter(k => fullDatum[k] !== undefined && fullDatum[k] !== null)
      .map(k => {
        const label = yKeyLabels?.[k] ?? k;
        const value =
          typeof fullDatum[k] === "number"
            ? Math.round(fullDatum[k] as number)
            : fullDatum[k];
        return `${label} : ${value}`;
      });
    return `${xValue}\n${lines.join("\n")}`;
  };

/**
 * Data structure for PlatformChart
 */
export interface PlatformChartData {
  [key: string]: string | number;
}

/**
 * Props for PlatformChartView component
 */
/**
 * Padding configuration object for chart edges
 */
export interface PaddingConfig {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

/**
 * Translate a domain padding onto Victory's web shape.
 *
 * Victory takes a single number or `{ x, y }`, where each entry may be one
 * value or a `[start, end]` pair. The per-side object form exists so a chart
 * can pad one edge without disturbing the others (SE-7139: the x-axis right
 * edge needs more room than the rest of the chart, and widening all four sides
 * to get it moves the y domain, which `.nice()` then re-buckets into a
 * different set of gridlines).
 * @param domainPadding - A single padding, or one value per side
 * @returns Victory's equivalent, or undefined when there is nothing to apply
 */
const toVictoryDomainPadding = (
  domainPadding: number | PaddingConfig
): number | { x: [number, number]; y: [number, number] } | undefined => {
  if (typeof domainPadding === "number") return domainPadding;
  return {
    x: [domainPadding.left, domainPadding.right],
    // Victory's y axis runs bottom-to-top, so `start` is the bottom edge.
    y: [domainPadding.bottom, domainPadding.top],
  };
};

/**
 * Builds the Skia chart's child renderer.
 *
 * A factory exported from here rather than a callback built in the Container:
 * it draws with `Line` and `Scatter` from victory-native, and importing those
 * into the Container pulled the package into that file's test module graph,
 * where jest cannot transform it. The Container memoizes the result.
 * @param yKeys - The series to draw.
 * @param colors - Colour per series, falling back to the default blue.
 * @returns The renderer CartesianChart calls with its resolved points.
 */
export const makeChartChildrenRenderer =
  (yKeys: readonly string[], colors: Record<string, string>) =>
  ({ points }: { points: Record<string, unknown> | null }) => {
    if (!points) return null;
    return yKeys.map((key, idx) => {
      const pointsForKey = (points as Record<string, unknown>)[key];

      if (!pointsForKey || !Array.isArray(pointsForKey)) return null;

      return (
        <React.Fragment key={`${key}-${idx}`}>
          <Line
            points={pointsForKey}
            color={colors[key] || "#3B82F6"}
            strokeWidth={2}
            connectMissingData
            curveType="natural"
          />
          <Scatter
            points={pointsForKey}
            color={colors[key] || "#3B82F6"}
            shape="circle"
            radius={3}
          />
        </React.Fragment>
      );
    });
  };

/**
 * Props for PlatformChartView component
 */
export interface PlatformChartViewProps {
  /** Array of data objects to be displayed in the chart */
  data: PlatformChartData[];
  /** Key name for x-axis values */
  xKey: string;
  /** Array of keys for y-axis values (multiple lines) */
  yKeys: string[];
  /** Color mapping for each y-axis key */
  colors: Record<string, string>;
  /** Optional human-readable labels for yKeys (for tooltips) */
  yKeyLabels?: Record<string, string>;
  /** Skia font for labels (native only) */
  font?: SkFont | null;
  /** Chart width in pixels */
  width: number;
  /** Chart height in pixels */
  height: number;
  /** Padding between domain and chart edge (number for web) */
  domainPadding: number | PaddingConfig;
  /** Padding around the chart (number for web) */
  padding: number;
  /** Mobile domain padding (object format for native) */
  mobileDomainPadding: PaddingConfig;
  /** Mobile padding (object format for native) */
  mobilePadding: PaddingConfig;
  /** X-axis configuration */
  xAxis?: {
    labelColor?: string;
    fontSize?: number;
    formatXLabel?: (value: string | number) => string;
    tickCount?: number;
    /**
     * Explicit x-axis tick positions (SE-7139). See the Container's prop docs
     * for why a tick *count* alone is not enough on native.
     */
    tickValues?: number[];
    axisValue?: number;
  };
  /** Y-axis configuration */
  yAxis?: {
    labelColor?: string;
    fontSize?: number;
    tickValues?: number[];
    tickFormat?: (value: number) => string;
  }[];
  /** Calculated Y domain for web [min, max] */
  webYDomain?: [number, number];
  /** Filtered data for web rendering */
  /**
   * Whether this is the web build. A capability check the Container resolves:
   * web draws with Victory over the DOM, native with Skia's CartesianChart.
   */
  readonly isWeb: boolean;
  /** Victory accessors per y-key, memoized by the Container. */
  readonly accessorsByKey: Record<
    string,
    {
      x: (datum: PlatformChartData) => string | number;
      y: (datum: PlatformChartData) => string | number;
    }
  >;
  /** Victory tooltip label formatter, memoized by the Container. */
  readonly tooltipLabelFormatter: ReturnType<
    typeof createTooltipLabelFormatter
  >;
  /** Skia chart children renderer, memoized by the Container. */
  readonly renderChartChildren: (args: {
    points: Record<string, unknown> | null;
  }) => React.ReactNode;
  /**
   * Native data with sparse rows removed.
   *
   * CartesianChart passes every yKey value to Skia as a numeric coordinate, and
   * a row missing a yKey yields `undefined`, which crashes it ("Value is
   * undefined, expected a number").
   */
  readonly nativeData: PlatformChartData[];
  webFilteredData: Map<string, PlatformChartData[]>;
}

/**
 * Renders the Victory line + scatter pair for a single yKey on web.
 * Extracted to keep `PlatformChartView`'s cognitive complexity below the
 * project threshold — the early-return guard counts toward complexity
 * inline.
 * @param args - Render arguments for one Victory series
 * @param args.key - The yKey identifier this series represents
 * @param args.index - Position in the yKeys array (drives scatter size)
 * @param args.validData - Filtered data for this key, or undefined when missing
 * @param args.color - Optional color override for stroke + fill
 * @param args.accessor - x/y accessor functions matched to this yKey
 * @param args.accessor.x - Accessor returning the x coordinate for a datum
 * @param args.accessor.y - Accessor returning the y coordinate for a datum
 * @returns A two-element array of Victory elements, or null when no data
 */
const renderVictorySeries = (args: {
  key: string;
  index: number;
  validData: PlatformChartData[] | undefined;
  color: string | undefined;
  accessor: {
    x: (datum: PlatformChartData) => string | number;
    y: (datum: PlatformChartData) => string | number;
  };
}): React.ReactElement[] | null => {
  const { key, index, validData, color, accessor } = args;
  if (!validData || validData.length === 0) {
    return null;
  }
  return [
    <VictoryLine
      key={`line-${key}`}
      name={`line-${key}`}
      data={validData}
      x={accessor.x}
      y={accessor.y}
      interpolation="natural"
      // eslint-disable-next-line no-restricted-syntax -- Victory chart library style API (data-driven stroke), not an RN/NativeWind style prop; non-atomizable
      style={victoryLineStyle(color)}
      animate={false}
    />,
    <VictoryScatter
      key={`scatter-${key}`}
      name={`scatter-${key}`}
      data={validData}
      x={accessor.x}
      y={accessor.y}
      size={index === 0 ? 4 : 3}
      // eslint-disable-next-line no-restricted-syntax -- Victory chart library style API (data-driven fill), not an RN/NativeWind style prop; non-atomizable
      style={victoryScatterStyle(color)}
      animate={false}
    />,
  ];
};

/**
 * Sanitizes data for the native (Skia) chart path. CartesianChart passes
 * every yKey value to Skia as a numeric coordinate, so sparse rows
 * (where any yKey is absent or non-finite) crash Skia. This filter
 * keeps only rows where every listed yKey has a finite numeric value.
 * @param data - Source data rows
 * @param yKeys - yKey identifiers that must all be present + finite
 * @returns Subset of `data` safe to pass to CartesianChart
 */
export const filterNativeData = (
  data: PlatformChartData[],
  yKeys: string[]
): PlatformChartData[] =>
  data.filter(d =>
    yKeys.every(k => {
      const v = d[k];
      return typeof v === "number" && Number.isFinite(v);
    })
  );

/**
 * The WEB chart: Victory over the DOM/SVG.
 * @param props - View props.
 * @returns The chart, or null until the container resolves the y-domain —
 *   rendering pre-resolve would draw an axis with no scale.
 */
const renderWebChart = (
  props: PlatformChartViewProps
): React.ReactElement | null => {
  // Web implementation using Victory (DOM/SVG). Bail until the
  // container resolves the y-domain — pre-resolve renders nothing.
  if (!props.webYDomain) return null;

  return (
    <VictoryChart
      width={props.width}
      height={props.height}
      domainPadding={toVictoryDomainPadding(props.domainPadding)}
      padding={typeof props.padding === "number" ? props.padding : undefined}
      domain={{ y: props.webYDomain }}
      containerComponent={
        <VictoryVoronoiContainer
          voronoiDimension="x"
          labels={props.tooltipLabelFormatter}
          voronoiBlacklist={[
            // Blacklist ALL scatter points to prevent duplicate tooltips
            ...props.yKeys.map(k => `scatter-${k}`),
            // Blacklist all lines except the first one
            ...props.yKeys.slice(1).map(k => `line-${k}`),
          ]}
          labelComponent={
            <VictoryTooltip
              cornerRadius={6}
              renderInPortal
              constrainToVisibleArea
              centerOffset={{ x: 0, y: -10 }}
              flyoutStyle={{
                fill: "#27272a",
                stroke: "#52525b",
                strokeWidth: 1,
              }}
              // eslint-disable-next-line no-restricted-syntax -- Victory chart library style API (per-series tooltip label descriptors), not an RN/NativeWind style prop; non-atomizable
              style={[
                {
                  fill: "#ffffff",
                  fontSize: 14,
                  fontFamily: SATOSHI_FONT_FAMILY,
                  fontWeight: "600",
                },
                ...props.yKeys.map(k => ({
                  fill: brightenColor(props.colors[k] || "#22d3ee"),
                  fontSize: 13,
                  fontFamily: SATOSHI_FONT_FAMILY,
                  fontWeight: "600",
                })),
              ]}
              flyoutPadding={{ top: 10, bottom: 10, left: 14, right: 14 }}
              pointerLength={6}
              labelComponent={<VictoryLabel lineHeight={1.5} />}
            />
          }
        />
      }
    >
      <VictoryAxis
        orientation="bottom"
        dependentAxis={false}
        tickFormat={props.xAxis?.formatXLabel ?? defaultTickFormat}
        {...(props.xAxis?.tickCount
          ? { tickCount: props.xAxis.tickCount }
          : {})}
        {...(props.xAxis?.tickValues?.length
          ? { tickValues: props.xAxis.tickValues }
          : {})}
        {...(props.xAxis?.axisValue !== undefined
          ? { axisValue: props.xAxis.axisValue }
          : {})}
        // eslint-disable-next-line no-restricted-syntax -- Victory chart library style API (data-driven axis labels), not an RN/NativeWind style prop; non-atomizable
        style={victoryAxisStyle(props.xAxis?.labelColor, props.xAxis?.fontSize)}
      />
      <VictoryAxis
        dependentAxis
        {...(props.yAxis?.[0]?.tickValues
          ? { tickValues: props.yAxis[0].tickValues }
          : {})}
        tickFormat={props.yAxis?.[0]?.tickFormat ?? defaultTickFormat}
        // eslint-disable-next-line no-restricted-syntax -- Victory chart library style API (data-driven axis labels), not an RN/NativeWind style prop; non-atomizable
        style={victoryAxisStyle(
          props.yAxis?.[0]?.labelColor,
          props.yAxis?.[0]?.fontSize
        )}
      />

      {props.yKeys.map((key, index) =>
        renderVictorySeries({
          key,
          index,
          validData: props.webFilteredData.get(key),
          color: props.colors[key],
          accessor: props.accessorsByKey[key],
        })
      )}
    </VictoryChart>
  );
};

/**
 * The NATIVE chart: Skia's CartesianChart.
 * @param props - View props.
 * @returns The chart, or a reserved-height box while it cannot draw.
 */
const renderNativeChart = (
  props: PlatformChartViewProps
): React.ReactElement | null => {
  // Sanitize data for native — CartesianChart passes every yKey value to Skia
  // as a numeric coordinate. Sparse rows (where a yKey is absent) produce
  // `undefined`, which crashes Skia ("Value is undefined, expected a number").
  // Filtering is hoisted into `filterNativeData` so the .every nested
  // inside .filter doesn't count toward this component's cognitive
  // complexity budget.
  // Combined native-empty-state guard: render an empty box when either
  // the Skia font hasn't loaded yet (`!font` would otherwise crash
  // CartesianChart) OR the sanitized data is empty. Combining the two
  // conditions keeps the component's cognitive complexity within budget.
  if (!props.font || props.nativeData.length === 0) {
    return (
      <Box
        className="w-full"
        // eslint-disable-next-line no-restricted-syntax -- measured/data-driven chart height; off the closed sizing ladder, no static token
        UNSAFE_style={{ height: props.height }}
      />
    );
  }

  return (
    <Box
      className="w-full overflow-visible"
      // eslint-disable-next-line no-restricted-syntax -- measured/data-driven chart height; off the closed sizing ladder, no static token
      UNSAFE_style={{ height: props.height }}
    >
      <CartesianChart
        data={props.nativeData}
        xKey={props.xKey}
        yKeys={props.yKeys as never[]}
        domainPadding={props.mobileDomainPadding}
        padding={props.mobilePadding}
        xAxis={{
          font: props.font,
          labelColor: props.xAxis?.labelColor || "#666",
          lineColor: "transparent",
          lineWidth: 0,
          labelPosition: "outset",
          labelOffset: 10,
          formatXLabel: props.xAxis?.formatXLabel || defaultTickFormat,
          ...(props.xAxis?.tickCount
            ? { tickCount: props.xAxis.tickCount }
            : {}),
          // SE-7139: pin the ticks to the values actually plotted. Guarded on
          // length, not just presence — victory-native treats an empty
          // `tickValues` as "draw no ticks", which would blank the axis.
          ...(props.xAxis?.tickValues?.length
            ? { tickValues: props.xAxis.tickValues }
            : {}),
        }}
        yAxis={[
          {
            font: props.font,
            labelColor: props.yAxis?.[0]?.labelColor || "#666",
            lineColor: "transparent",
            lineWidth: 0,
          },
        ]}
      >
        {props.renderChartChildren}
      </CartesianChart>
    </Box>
  );
};

/**
 * Renders the platform's chart.
 *
 * The fork is a CAPABILITY check, not a screen-size one: web has the DOM and
 * uses Victory, native has Skia. The Container resolves it (SE-8019), and the
 * repo's screen-size rule keeps this class of platform check by name.
 * @param props - View props.
 * @returns The chart.
 */
const PlatformChartView = (props: PlatformChartViewProps) =>
  props.isWeb ? renderWebChart(props) : renderNativeChart(props);

PlatformChartView.displayName = "PlatformChartView";

export default React.memo(PlatformChartView);
